/**
 * ParPay's frame speaks in `key=value` strings, and this is the one place that knows it.
 *
 * The rest of the embedded path is vendor-neutral on purpose: the server tells the client which handshake to
 * send precisely so the client never has to know which gateway it is talking to. This file is the exception,
 * and it is deliberate rather than an oversight.
 *
 * The alternative is describing the wire format on `EmbeddedPaymentAction` alongside `handshake`, which means
 * a proto field, two NuGet publishes and four repos — to move two string literals across a boundary, for the
 * only embedded gateway that exists. A generic message-format contract with one implementation would also be
 * a guess at what a second gateway needs, and the next one is as likely to post structured objects as it is
 * `key=value` strings.
 *
 * So: if a second embedded gateway ever arrives, this function is the seam to lift into the contract, and
 * this comment is the note explaining what was traded away.
 *
 * The three messages, from the legacy consumer site's handler:
 *
 *   frame → host   enablePlaceOrder=true|false   whether the card fields are valid yet
 *   host  → frame  aurus-token                   the guest pressed Pay; release the token
 *   frame → host   response={…json…}             the token, or a refusal, for the server to judge
 */

/** The card fields became valid or invalid. Governs whether Pay can be pressed. */
interface ValidityMessage {
    kind: 'validity';
    canSubmit: boolean;
}

/** The gateway's answer to the handshake, for forwarding to `/payments/{id}/complete` unread. */
interface ResultMessage {
    kind: 'result';
    payload: Record<string, unknown>;
}

/** Nothing this client acts on, including anything it could not parse. */
interface IgnoredMessage {
    kind: 'ignored';
}

export type GatewayMessage = ValidityMessage | ResultMessage | IgnoredMessage;

const IGNORED: GatewayMessage = { kind: 'ignored' };

const VALIDITY_PREFIX = 'enablePlaceOrder=';
const RESULT_PREFIX = 'response=';

/**
 * Reads whatever the frame posted.
 *
 * Objects are passed through as a result, which is what every gateway that posts structured data will do and
 * what this component originally assumed universally. Strings are ParPay's dialect.
 *
 * Only the prefixes above are understood; anything else is ignored rather than guessed at. A payload that
 * claims to be a result but is not valid JSON is ignored too — forwarding a half-read gateway response to an
 * endpoint that charges cards is worse than forwarding nothing, and the caller re-enables Pay so the guest is
 * not stranded.
 *
 * Note the split on the FIRST `=` only: a base64 field inside the JSON can and does contain `=`, and the
 * legacy client's `split('=')[1]` truncates exactly those payloads.
 */
export function readGatewayMessage(data: unknown): GatewayMessage {
    if (typeof data === 'object' && data !== null) {
        return { kind: 'result', payload: data as Record<string, unknown> };
    }

    if (typeof data !== 'string') return IGNORED;

    if (data.startsWith(VALIDITY_PREFIX)) {
        return { kind: 'validity', canSubmit: data.slice(VALIDITY_PREFIX.length) === 'true' };
    }

    if (data.startsWith(RESULT_PREFIX)) {
        const body = data.slice(RESULT_PREFIX.length);

        try {
            const parsed: unknown = JSON.parse(body);

            // A bare string or number is not a result. Only an object can carry a token.
            if (typeof parsed !== 'object' || parsed === null) return IGNORED;

            return { kind: 'result', payload: parsed as Record<string, unknown> };
        } catch {
            return IGNORED;
        }
    }

    return IGNORED;
}
